import React, { useEffect, useRef, useState } from 'react';
import './galeria.css';

const dateLabel = (date) => new Intl.DateTimeFormat('es-CO', { dateStyle: 'long' }).format(new Date(`${date}T12:00:00`));
const apiUrl = `${process.env.PUBLIC_URL || ''}/api/galeria.php`;

export default function Galeria() {
  const [albums, setAlbums] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const album = albums.find((item) => item.date === selected);
  const photos = album?.photos || [];

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    (async () => {
      try {
        // Una URL nueva también evita respuestas antiguas de proxies del hosting.
        const requestUrl = new URL(apiUrl, window.location.origin);
        requestUrl.searchParams.set('revision', `${Date.now()}-${reload}`);
        const response = await fetch(requestUrl.toString(), { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('No fue posible consultar los álbumes.');
        const data = await response.json();
        if (!Array.isArray(data.albums)) throw new Error('La respuesta de la galería no es válida.');
        const available = data.albums.filter((item) => /^\d{4}-\d{2}-\d{2}$/.test(item.date) && !Number.isNaN(new Date(`${item.date}T12:00:00`).getTime()) && Array.isArray(item.photos) && item.photos.length > 0).sort((a, b) => b.date.localeCompare(a.date));
        setAlbums(available);
        setSelected((previous) => available.some((item) => item.date === previous) ? previous : available[0]?.date || '');
      } catch (failure) {
        if (failure.name !== 'AbortError') setError('No pudimos cargar la galería. Intenta nuevamente en unos momentos.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [reload]);

  useEffect(() => {
    if (!viewerOpen) return undefined;
    const element = dialog.current;
    const previousOverflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      trigger.current?.focus();
    };
  }, [viewerOpen]);

  const move = (amount) => setPhotoIndex((index) => (index + amount + photos.length) % photos.length);

  return (
    <section className="capsos-gallery" aria-labelledby="gallery-title">
      <div className="container">
        <header className="gallery-heading">
          <span>GALERÍA CAPSOS</span>
          <h1 id="gallery-title">Momentos que nos conectan.</h1>
          <p>Recorre nuestros álbumes y descubre lo que compartimos con nuestra comunidad.</p>
        </header>
        <div className="gallery-toolbar">
          <div><label htmlFor="gallery-date">Selecciona la fecha del álbum</label>
            <select id="gallery-date" value={selected} disabled={loading || !albums.length || !!error} onChange={(event) => setSelected(event.target.value)}>
              {!albums.length && <option value="">Sin álbumes disponibles</option>}
              {albums.map((item) => <option key={item.date} value={item.date}>{dateLabel(item.date)}</option>)}
            </select>
          </div>
          <button type="button" onClick={() => setReload((value) => value + 1)} disabled={loading}>{loading ? 'Cargando…' : 'Actualizar álbumes'}</button>
        </div>
        {loading && <p role="status">Buscando momentos para compartir…</p>}
        {error && <p role="alert">{error}</p>}
        {!loading && !error && albums.length > 0 && <p className="gallery-available" role="status">{albums.length} {albums.length === 1 ? 'álbum disponible' : 'álbumes disponibles'}. Selecciona una fecha para ver sus fotografías.</p>}
        {!loading && !error && !album && <p role="status">Pronto compartiremos nuevas fotografías. Vuelve a visitarnos.</p>}
        {!loading && !error && album && <>
          <div className="gallery-album-heading" aria-live="polite"><h2>{dateLabel(album.date)}</h2><span>{photos.length} {photos.length === 1 ? 'fotografía' : 'fotografías'}</span></div>
          <div className="gallery-grid">
            {photos.map((photo, index) => <button type="button" className="gallery-photo" key={photo.url} aria-label={`Ampliar fotografía ${index + 1} del ${dateLabel(album.date)}`} onClick={(event) => { trigger.current = event.currentTarget; setPhotoIndex(index); setViewerOpen(true); }}>
              <img src={photo.url} alt={`Álbum del ${dateLabel(album.date)}, fotografía ${index + 1}`} loading="lazy" decoding="async" />
              <span>Ver fotografía ↗</span>
            </button>)}
          </div>
        </>}
        {viewerOpen && <dialog ref={dialog} className="gallery-dialog" aria-label="Visor de fotografías" onCancel={() => setViewerOpen(false)} onClick={(event) => { if (event.target === event.currentTarget) setViewerOpen(false); }} onKeyDown={(event) => { if (event.key === 'ArrowRight') { event.preventDefault(); move(1); } if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); } }}>
          <div className="gallery-viewer">
            <button type="button" className="gallery-close" onClick={() => setViewerOpen(false)} autoFocus aria-label="Cerrar visor">✕</button>
            <img src={photos[photoIndex].url} alt={`Fotografía ${photoIndex + 1} del álbum del ${dateLabel(album.date)}`} />
            <div className="gallery-viewer-controls">
              <button type="button" disabled={photos.length < 2} onClick={() => move(-1)} aria-label="Fotografía anterior">←</button>
              <span aria-live="polite">{photoIndex + 1} / {photos.length}</span>
              <button type="button" disabled={photos.length < 2} onClick={() => move(1)} aria-label="Fotografía siguiente">→</button>
            </div>
          </div>
        </dialog>}
      </div>
    </section>
  );
}

